/**
 * @file Renders the modal that reads and updates an entity through a form.
 */
import React, { useState } from "react";
import { Button } from "antd";
import { StopOutlined } from "@ant-design/icons";
import type { FormInstance } from "antd";

import { W1 } from "@components/Constants";
import { EntityForm } from "@components/Form";
import { useAppContext } from "@components/AppContext";
import type { AppContext } from "@components/AppContext/types";
import { wait, type Operation } from "@api";

import Modal from "./Modal";
import type { EntityItem, ModalRef, TableRef } from "./types";

type EntityFormPassthroughProps = {
  entity: string;
  operation: Operation;
  schema?: unknown;
  item?: unknown;
  form?: FormInstance;
  request?: AppContext["request"];
  submitLabel?: string;
  onSubmit?: (parameters: unknown) => unknown;
  submitButtonProps?: Record<string, unknown>;
  successMessage?: string | boolean;
  failedMessage?: string | boolean;
  shouldOptimizeUpdateMutation?: boolean;
  expectedErrors?: string[];
  formProps?: Record<string, unknown>;
  hasSubmitButton?: boolean;
  onFailure?: () => void;
  onSubmitFailed?: () => void;
  onSubmitConfirm?: (parameters: unknown, action: () => unknown) => unknown;
  submitItemProps?: Record<string, unknown>;
};

type ReadResponse = {
  data: EntityItem;
};

type Props = {
  onSuccess?: (updatedItem: unknown) => unknown;
  table?: TableRef;
  width?: number | string;
  footer?: (item?: EntityItem) => React.ReactNode;
  onOpen?: (item: EntityItem) => void;
  onItemRead?: (item: EntityItem) => void;
  deleteTitle?: string;
  deleteAction?: (item?: EntityItem) => unknown;
  readOperation?: Operation;
  modal: ModalRef;
  title: string;
} & EntityFormPassthroughProps;

/**
 * Renders a modal that reads an entity by id, shows it in an update form,
 * and optionally deletes it or reports the update to the given table.
 */
const UpdateModal = ({
  onSuccess: onSuccessCustom = undefined,
  table = undefined,
  width = undefined,
  footer = undefined,
  onOpen = undefined,
  onItemRead = undefined,
  deleteTitle = undefined,
  deleteAction = undefined,
  readOperation = undefined,
  modal,
  title,
  ...otherEntityFormProps
}: Props): JSX.Element => {
  const { request } = useAppContext() as AppContext;

  const [ item, setItem ] = useState<EntityItem>();
  const [ isOpen, setIsOpen ] = useState(false);
  const [ isRemoving, setIsRemoving ] = useState(false);

  /**
   * Closes the modal and clears the loaded item once it has faded out.
   */
  const onClose = async () => {
    setIsOpen(false);

    await wait();
    setItem(undefined);
  };

  /**
   * Runs the custom success handler, reports the update to the table and
   * closes the modal.
   */
  const onSuccess = async (updatedItem: unknown) => {
    if (onSuccessCustom) {
      await onSuccessCustom(updatedItem);
    }

    if (table) {
      table.current.updateItem(item.id, updatedItem);
    }

    return onClose();
  };

  /**
   * Opens the modal and loads the entity with the given id through
   * `readOperation`.
   */
  modal.current.open = (id: string) => {
    setIsOpen(true);

    if (!readOperation) {
      const message = "Prop \"readOperation\" is not defined, use .openItem(item) operation instead.";
      throw new Error(message);
    }

    return request<ReadResponse>(readOperation, { id })
      .then(({ data }) => {
        setItem(data);

        if (onItemRead) {
          onItemRead(data);
        }
      });
  };

  /**
   * Closes the modal.
   */
  modal.current.close = () =>
    setIsOpen(false);

  /**
   * Opens the modal with an item that is already loaded, instead of
   * reading it through `readOperation`.
   */
  modal.current.openItem = async (customItem: Record<string, unknown>) => {
    setItem({ ...customItem });

    if (onOpen) {
      onOpen({ ...customItem });
    }

    setIsOpen(true);
  };

  let deleteFooter;

  if (deleteAction) {
    /**
     * Deletes the loaded item and tracks the delete button's loading state.
     */
    const onDelete = async () => {
      setIsRemoving(true);
      await deleteAction(item);
      setIsRemoving(false);
    };

    deleteFooter = (
      <div style={{ textAlign: "center", marginTop: W1 - 6 }}>
        <Button
          danger
          type="text"
          icon={<StopOutlined />}
          onClick={onDelete}
          loading={isRemoving}
        >
          {deleteTitle}
        </Button>
      </div>
    );
  }

  const modalProps = {
    forceRender: true,
    destroyOnClose: true,
    width,
    title,
    isOpen,
    onClose,
  };

  return (
    <Modal {...modalProps}>
      <EntityForm
        item={item}
        setItem={setItem}
        onSuccess={onSuccess}
        {...otherEntityFormProps}
      />

      {footer && footer(item)}

      {deleteAction && deleteFooter}
    </Modal>
  );
};

export default UpdateModal;
